import { useCallback } from "react";
import useSWR from "swr";
import { useApi } from "../api";
import { CustomError } from "../errors";
import { CustomTaskCategory } from "./types";

export const useCustomTasksApi = () => {
  const api = useApi();

  const useAdminSubsidiaryCustomTasks = useCallback(
    (subsidiaryId: string, signal: AbortSignal) => {
      const url = `/admin/subsidiaries/${subsidiaryId}/custom_tasks`;
      return useSWR<CustomTaskCategory[] | null, CustomError, string>(url, (url) =>
        api.get<CustomTaskCategory[]>({ url, signal })
      );
    },
    [api]
  );

  const initAdminSubsidiaryCustomTasks = useCallback(
    async (subsidiaryId: string, categoryId: string) => {
      const url = `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}`;
      await api.put<undefined>(url, undefined).then(() => {
        api.mutateCache([
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks`,
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}/tasks`,
        ]);
      });
    },
    [api]
  );

  const updateAdminSubsidiaryCustomTasks = useCallback(
    async (subsidiaryId: string, categoryId: string, tasks: string[]) => {
      const url = `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}/tasks`;
      await api.put<string[]>(url, tasks).then(() => {
        api.mutateCache([
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks`,
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}/tasks`,
        ]);
      });
    },
    [api]
  );

  const deleteAdminSubsidiaryCustomTasks = useCallback(
    async (subsidiaryId: string, categoryId: string) => {
      const url = `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}`;
      await api.del(url).then(() => {
        api.mutateCache([
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks`,
          `/admin/subsidiaries/${subsidiaryId}/custom_tasks/${categoryId}/tasks`,
        ]);
      });
    },
    [api]
  );

  return {
    useAdminSubsidiaryCustomTasks,
    initAdminSubsidiaryCustomTasks,
    updateAdminSubsidiaryCustomTasks,
    deleteAdminSubsidiaryCustomTasks,
  };
};
